import { useMutation } from "@apollo/client";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { useFormik } from "formik";
import {
  ButtonBack,
  ButtonNext,
  CarouselProvider,
  Image,
  Slide,
  Slider,
} from "pure-react-carousel";
import { useEffect, useState } from "react";

import { UPDATE_IMAGE as updateImageMutation } from "../../../graphql/mutation/Image";
import { usePhotoMaterialLazy } from "../../../hooks/usePhotoMaterial";
import { GridInputs } from "../../Functional";
import { Button, IconButton, Modal } from "../../ui";

const disableConfig = {
  disabled: true,
  inputClassName: "!bg-white !text-gray-400 !border-gray-400",
};
const UpdateImageInfo = props => {
  const { images: propsImages = [], currentSlide, artworkId } = props;
  const [selectedImageIdx, setSelectedImageIdx] = useState(null);
  const [updateImage, { loading, error, reset }] = useMutation(
    updateImageMutation(),
    { refetchQueries: ["FetchArtwork", "FetchInstance"] },
  );
  const [fetchPhotoMaterialQuery, { photoMaterials }] = usePhotoMaterialLazy(
    { format: true },
    { fetchPolicy: "network-only" },
  );

  useEffect(() => {
    if (artworkId) {
      fetchPhotoMaterialQuery({
        _artwork: {
          id: {
            operator: "eq",
            value: artworkId,
          },
        },
      });
    }
  }, [artworkId, fetchPhotoMaterialQuery]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      ...(selectedImageIdx
        ? propsImages?.[selectedImageIdx]
        : propsImages?.[currentSlide]),
      totalImages: propsImages.length,
    },
    onSubmit: async values => {
      const {
        archiveId,
        artworkId,
        channelDisplayOnline,
        photoMaterialId,
        photoMaterial,
        description,
        order,
      } = values;

      try {
        await updateImage({
          variables: {
            input: {
              archiveId,
              artworkId,
              channelDisplayOnline,
              photoMaterialId: photoMaterial?.id || photoMaterialId,
              description,
              order,
            },
          },
        });

        reset();
      } catch (e) {
        console.error("updateImage: ", e);
      }
    },
  });

  const inputs = {
    className: "grid gap-2",
    inputs: [
      {
        inputs: [
          {
            label: "Photographer (Description) (Format)",
            name: "photoMaterial.id",
            type: "multi-select",
            options:
              photoMaterials?.map(item => {
                return {
                  label: `${item?.photographer} (${item?.description}) (${item?.format?.description})`,
                  value: item?.id,
                };
              }) || [],
          },
        ],
      },
      {
        className: "grid-cols-1 gap-4 lg:grid-cols-2",
        inputs: [
          {
            label: "Credit",
            name: "photoMaterial.credit",
            type: "text",
            ...disableConfig,
          },
          {
            label: "Archive ID",
            name: "archiveId",
            type: "text",
            ...disableConfig,
          },
          {
            name: "channelDisplayOnline",
            type: "select",
            options: [
              { label: "Display Online", value: 1 },
              { label: "Not for Display Online", value: 0 },
            ],
          },
          {
            className: "grid-cols-1 gap-4 sm:grid-cols-2",
            inputs: [
              { label: "Order", name: "order", type: "number" },
              {
                label: "Of",
                name: "totalImages",
                type: "text",
                ...disableConfig,
              },
            ],
          },
        ],
      },
      {
        inputs: [
          {
            label: "Image Notes",
            name: "description",
            type: "textarea",
            disabled: false,
          },
        ],
      },
    ],
  };
  const manageImageInputs = {
    formik,
    ...inputs,
  };

  const handleImageSelection = index => e => {
    e.preventDefault();
    e.stopPropagation();

    setSelectedImageIdx(() => index);
  };

  const handleModalOpen = () => {
    setSelectedImageIdx(currentSlide);
  };

  const modalProps = {
    scale: "md",
    closeOnBackdrop: false,
    hideCloseButton: true,
    onOpen: handleModalOpen,
    body: ({ closeModal }) => {
      return (
        <div className="flex w-full min-w-0 flex-col px-0 sm:px-6">
          <div className="flex flex-col gap-4 lg:flex-row">
            <div className="flex flex-1 flex-col text-xl">
              Manage Artwork Images
              <div className={`mt-2 text-sm font-normal`}>
                {propsImages.length} images
              </div>
              {error && (
                <div className={`text-sm text-red-500`}>{error?.message}</div>
              )}
            </div>
            <div className="flex flex-wrap justify-end gap-3 lg:gap-4">
              <Button
                className="h-9 w-full sm:!w-32"
                action="default"
                label="Cancel"
                onClick={() => {
                  closeModal();
                  reset();
                }}
              />
              <Button
                className="flex h-9 w-full items-center sm:!w-32"
                label={loading ? "Saving" : "Save"}
                disabled={!formik.isValid || loading || !formik?.dirty}
                onClick={formik.handleSubmit}
              />
            </div>
          </div>
          <div className="mt-4">
            <div className="border-b border-gray-100 pb-6">
              {propsImages?.length > 0 ? (
                <div className="max-w-full overflow-x-auto">
                  <div className="min-w-[42rem] lg:min-w-0">
                    <CarouselProvider
                      visibleSlides={4}
                      totalSlides={propsImages.length}
                      naturalSlideWidth={275}
                      naturalSlideHeight={200}
                      hasMasterSpinner={true}
                      className="flex w-full min-w-0 justify-between"
                    >
                      <ButtonBack>
                        <IconButton variant="clean" title="Back">
                          <ChevronLeftIcon
                            className={`h-5 w-5 cursor-pointer`}
                          />
                        </IconButton>
                      </ButtonBack>
                      <Slider className="min-w-0 flex-1 gap-4 lg:w-11/12 lg:flex-none lg:gap-16">
                        {propsImages?.map((image, index) => {
                          const imageSelected = selectedImageIdx === index;
                          const firstImage = index === 0;

                          return (
                            <Slide
                              index={index}
                              key={image?.archiveId ?? `dvc_image_${index}`}
                              className={`group relative`}
                            >
                              <div
                                className={`grid h-full w-full grid-rows-6 gap-1 px-4`}
                              >
                                <div
                                  className={`relative row-span-5 w-full cursor-pointer
                               ${firstImage ? "bg-gray-200 px-4 py-3" : ""}
                                ${imageSelected ? "border-4 border-black" : ""}`}
                                  onClick={handleImageSelection(index)}
                                >
                                  {image?.imgT && (
                                    <Image
                                      src={image.imgT}
                                      className={`box-border object-cover ${firstImage ? "shadow-md shadow-gray-500" : ""}`}
                                    />
                                  )}
                                </div>
                                <div
                                  className={`row-span-1 flex justify-${imageSelected || firstImage ? "between" : "end"} block text-sm font-medium text-black`}
                                >
                                  {imageSelected ? (
                                    <span>Selected</span>
                                  ) : firstImage ? (
                                    <span className={`text-gray-400`}>
                                      Primary
                                    </span>
                                  ) : (
                                    ""
                                  )}
                                  {index + 1}
                                </div>
                              </div>
                            </Slide>
                          );
                        })}
                      </Slider>
                      <ButtonNext>
                        <IconButton variant="clean" title="Next">
                          <ChevronRightIcon
                            className={`h-5 w-5 cursor-pointer`}
                          />
                        </IconButton>
                      </ButtonNext>
                    </CarouselProvider>
                  </div>
                </div>
              ) : (
                <div
                  className={`text-md flex h-full w-full items-center justify-center text-gray-400`}
                >
                  NO IMAGES TO SHOW
                </div>
              )}
            </div>
            <div className={`my-6 text-xl`}>
              Update Information for Selected Image
            </div>
            <div>
              <GridInputs {...manageImageInputs} />
            </div>
          </div>
        </div>
      );
    },
  };

  return (
    <Modal {...modalProps}>
      <Button label="Update Info" className={"!w-32"} />
    </Modal>
  );
};

export default UpdateImageInfo;
